import { useGetIdentity, useLogout } from "@refinedev/core";
import {
  ArrowLeftRight,
  ArrowUpFromLine,
  Barcode,
  Boxes,
  ChevronRight,
  ClipboardCheck,
  ClipboardList,
  Inbox,
  ListChecks,
  LogOut,
  Moon,
  Package,
  PackageCheck,
  PackageMinus,
  PackageOpen,
  ScanLine,
  Settings,
  ShieldAlert,
  Shirt,
  Sun,
  Tag,
  Truck,
  Warehouse,
} from "lucide-react";
import type { ReactNode } from "react";
import { useState } from "react";
import { NavLink, useLocation } from "react-router";

import { Annotator } from "@/components/Annotator";
import type { CurrentUser } from "@/lib/providers";

/**
 * Пункт меню. `to` есть только у того, что действительно открывается:
 * остальные разделы склада названы, но никуда не ведут — и выглядят
 * соответственно. Ссылка, которая никуда не ведёт, хуже honest-заглушки:
 * по ней жмут дважды, прежде чем понять, что она мертва.
 */
type NavItem = {
  label: string;
  icon: ReactNode;
  to?: string;
  /**
   * Пункт активен ТОЛЬКО на своём адресе, а не на вложенных. Нужен «Расчёту»:
   * адрес `/podsort` — начало `/podsort/tasks`, и без `end` на экране задач
   * горели бы оба пункта группы.
   */
  end?: boolean;
};

const NAV: { group: string; items: NavItem[] }[] = [
  {
    group: "Склад",
    items: [
      { label: "Товары", icon: <Shirt size={14} />, to: "/products" },
      // Три соседних вопроса склада, и разными их делает объект: «Товары» —
      // про модель, «Баркод» — про один размер, «Серия» — про одну вещь.
      { label: "Баркод", icon: <Barcode size={14} />, to: "/barcode" },
      { label: "Серия", icon: <Tag size={14} />, to: "/serial" },
      { label: "Короба", icon: <Package size={14} />, to: "/boxes" },
      // Соседний пункт «Коробам», но склад у него другой: там короба
      // ХРАНЕНИЯ, здесь короба ОТГРУЗКИ маркетплейсам. Стоят рядом, потому
      // что рядом стоят и на складе.
      { label: "FBS Короба", icon: <PackageOpen size={14} />, to: "/fbs" },
      // Задача СБОРКИ, а не короб: сюда приходят из ленты движений, когда
      // нужно понять, каким действием вещь попала в короб отгрузки и что с
      // этим заказом у маркетплейса.
      { label: "FBS Задача", icon: <ClipboardList size={14} />, to: "/fbs-task" },
      // Стоит третьим в ряду FBS, а не в «Служебном»: раздел надзорный, но
      // ищут его глазами рядом с тем, за чем он надзирает.
      { label: "FBS Контроль", icon: <ShieldAlert size={14} />, to: "/fbs-control" },
      { label: "Приёмка", icon: <Inbox size={14} /> },
      { label: "Размещение", icon: <Boxes size={14} />, to: "/placement" },
      { label: "Хранение", icon: <Warehouse size={14} /> },
    ],
  },
  {
    // ПОДСОРТ — довоз товара на склад маркетплейса взамен проданного, ВБ и
    // Ozon. Своя группа меню (владелец 17.09.26): у раздела два экрана —
    // «сколько везти» и «что уже поставлено в работу», — и стоят они рядом,
    // потому что второй продолжает первый.
    group: "Подсорт",
    items: [
      {
        label: "Расчёт",
        icon: <ArrowUpFromLine size={14} />,
        to: "/podsort",
        end: true,
      },
      { label: "Задачи", icon: <ListChecks size={14} />, to: "/podsort/tasks" },
    ],
  },
  {
    group: "Операции",
    items: [
      // Раздел про ОПЕРАЦИЮ — что склад снял с учёта и откуда.
      // `end` — итоги живут под тем же адресом (`/spisanie/summary`), и без
      // него подсвечивались бы оба пункта сразу.
      { label: "Списание", icon: <PackageMinus size={14} />, to: "/spisanie", end: true },
      // Итог того же: сколько списано и оприходовано по городам за период.
      // «Оприходование», а не «Приход»: приход на складе — поставка и приёмка,
      // а их здесь нет (владелец 29.09.26).
      {
        label: "Оприходование и списание",
        icon: <ArrowLeftRight size={14} />,
        to: "/spisanie/summary",
      },
      { label: "Отбор", icon: <ScanLine size={14} /> },
      { label: "Упаковка", icon: <PackageCheck size={14} /> },
      { label: "Отгрузка", icon: <Truck size={14} /> },
    ],
  },
  {
    group: "Служебное",
    items: [
      { label: "Инвентаризация", icon: <ClipboardCheck size={14} /> },
      { label: "Настройки", icon: <Settings size={14} /> },
    ],
  },
];

const ITEM_BASE =
  "flex h-8 w-full items-center gap-2.5 rounded-[var(--radius-ui)] px-2 text-left text-[13.5px] transition-colors";

/**
 * Название открытого раздела для крошек. Считается из АДРЕСА, а не хранится
 * рядом с ним: прибитая строка «Размещение» осталась бы висеть над списком
 * товаров, и крошки врали бы ровно там, где нужны, — при переходе между
 * разделами.
 *
 * Ключи — начала пути, поэтому `/products/14623` попадает в «Товары» без
 * отдельной записи. Порядок значения не имеет: разделы не вложены друг в
 * друга.
 */
const SECTION: { prefix: string; label: string }[] = [
  { prefix: "/products", label: "Товары" },
  { prefix: "/placement", label: "Размещение" },
  { prefix: "/serial", label: "Серия" },
  { prefix: "/barcode", label: "Баркод" },
  { prefix: "/boxes", label: "Короба" },
  { prefix: "/fbs", label: "FBS Короба" },
  // Отдельная запись, а не хвост «/fbs»: сопоставление идёт по полному
  // сегменту (`=== prefix` либо `prefix + "/"`), и «/fbs-task» в «/fbs» не
  // попадает — иначе карточка задачи звалась бы в крошках «FBS Короба».
  { prefix: "/fbs-task", label: "FBS Задача" },
  { prefix: "/fbs-control", label: "FBS Контроль" },
  // Задачи ВЫШЕ расчёта: `find` берёт первое совпадение, а `/podsort/tasks`
  // начинается с `/podsort/` — стоя ниже, экран задач звался бы «Расчётом».
  { prefix: "/podsort/tasks", label: "Подсорт · Задачи" },
  { prefix: "/podsort", label: "Подсорт · Расчёт" },
  { prefix: "/spisanie/summary", label: "Оприходование и списание" },
  { prefix: "/spisanie", label: "Списание" },
];

export function sectionTitle(pathname: string): string {
  return (
    SECTION.find((s) => pathname === s.prefix || pathname.startsWith(s.prefix + "/"))
      ?.label ?? "wms"
  );
}

/**
 * Каркас кабинета.
 *
 * Приложение занимает ровно экран, а прокручивается только рабочая область:
 * так шапка таблицы и панель отбора остаются перед глазами.
 *
 * На узком экране боковое меню убирается совсем, а не сжимается в иконки.
 * Отчёт открывают в проходе между стеллажами, где важен сам отчёт, — меню
 * из одного живого пункта отняло бы там треть ширины.
 */
export function AppLayout({ children }: { children: ReactNode }) {
  const { data: user } = useGetIdentity<CurrentUser>();
  const { mutate: logout } = useLogout();
  const { pathname } = useLocation();
  const [theme, setTheme] = useState<"dark" | "light">(
    () =>
      (document.documentElement.dataset["theme"] as "dark" | "light") ?? "dark",
  );

  const toggleTheme = () => {
    const next = theme === "dark" ? "light" : "dark";
    document.documentElement.dataset["theme"] = next;
    setTheme(next);
  };

  return (
    <div
      className="grid h-screen grid-cols-1 overflow-hidden md:grid-cols-[var(--spacing-sidebar)_minmax(0,1fr)]"
      data-component="AppLayout"
      data-annot="кабинет"
    >
      <aside className="hidden h-screen flex-col border-r border-border bg-bg-subtle md:flex">
        <div className="flex h-[var(--spacing-topbar)] flex-none items-center gap-2.5 border-b border-border px-4">
          <div className="grid size-[22px] place-items-center rounded-[5px] bg-fg text-[12px] font-semibold text-bg">
            W
          </div>
          <div className="text-[14px] font-semibold tracking-[-0.015em]">
            wms
          </div>
          <span className="ml-auto rounded border border-border px-1.5 font-mono text-[10px] text-fg-muted">
            склад
          </span>
        </div>

        <nav data-annot="меню" className="flex-1 overflow-y-auto p-2">
          {NAV.map((group) => (
            <div key={group.group} className="mb-4">
              <div className="px-2 pb-1.5 text-[10.5px] font-medium uppercase tracking-[0.06em] text-fg-muted">
                {group.group}
              </div>
              {group.items.map((item) =>
                item.to ? (
                  <NavLink
                    key={item.label}
                    to={item.to}
                    end={item.end ?? false}
                    className={({ isActive }) =>
                      ITEM_BASE +
                      (isActive
                        ? " bg-bg-hover font-medium text-fg"
                        : " text-fg-muted hover:bg-bg-hover hover:text-fg")
                    }
                  >
                    {item.icon}
                    {item.label}
                  </NavLink>
                ) : (
                  /* Раздел назван, но ещё не сделан. `disabled` — чтобы это
                     дошло и до клавиатуры, и до скринридера, а не только до
                     глаза, различившего приглушённый цвет. */
                  <button
                    key={item.label}
                    type="button"
                    disabled
                    title="Раздел ещё не сделан"
                    className={ITEM_BASE + " cursor-default text-fg-muted opacity-55"}
                  >
                    {item.icon}
                    {item.label}
                  </button>
                ),
              )}
            </div>
          ))}
        </nav>

        <div className="flex flex-none items-center gap-2 border-t border-border p-2">
          <div className="grid size-[22px] flex-none place-items-center rounded-full bg-accent text-[10px] font-semibold text-bg">
            {(user?.short ?? "").slice(0, 1).toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="truncate text-[12.5px]">{user?.name ?? "…"}</div>
            <div className="text-[11px] text-fg-muted">склад</div>
          </div>
          <button
            type="button"
            onClick={() => logout()}
            title="Выйти"
            aria-label="Выйти"
            className="ml-auto rounded p-1 text-fg-muted hover:bg-bg-hover hover:text-fg"
          >
            <LogOut size={13} />
          </button>
        </div>
      </aside>

      <div className="flex min-h-0 min-w-0 flex-col">
        <header
          data-annot="шапка"
          className="sticky top-0 z-30 flex h-[var(--spacing-topbar)] flex-none items-center gap-3 border-b border-border bg-bg/85 px-5 backdrop-blur"
        >
          <div className="flex min-w-0 items-center gap-2 text-[13.5px] text-fg-muted">
            <span className="hidden sm:inline">uni-renter</span>
            <ChevronRight
              size={13}
              className="hidden text-border-strong sm:inline"
            />
            <span>wms</span>
            <ChevronRight size={13} className="text-border-strong" />
            <span className="font-medium text-fg">
              {sectionTitle(pathname)}
            </span>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={toggleTheme}
              title={theme === "dark" ? "Светлая тема" : "Тёмная тема"}
              aria-label={theme === "dark" ? "Светлая тема" : "Тёмная тема"}
              className="grid h-8 w-8 place-items-center rounded-[var(--radius-ui)] border border-border hover:bg-bg-hover"
            >
              {theme === "dark" ? <Sun size={14} /> : <Moon size={14} />}
            </button>
            {/* На узком экране бокового меню нет, а выйти надо уметь. */}
            <button
              type="button"
              onClick={() => logout()}
              title="Выйти"
              aria-label="Выйти"
              className="grid h-8 w-8 place-items-center rounded-[var(--radius-ui)] border border-border hover:bg-bg-hover md:hidden"
            >
              <LogOut size={14} />
            </button>
          </div>
        </header>

        {children}
      </div>

      {/* Разметка живёт в КАРКАСЕ, а не на экране: замечание оставляют там,
          где его увидели, а видят его на любом разделе. */}
      <Annotator />
    </div>
  );
}
